import { safeGtagEvent } from '../apps/analytics/track';
import { isDoNotTrackEnabled } from './doNotTrack';
import { drainAndDelete, enqueue, reEnqueueMany } from './queue';
import { sendBatchBeacon, sendBatchFetch } from './transport';
import type { AnalyticsEventPayload, EventName } from './events';

/**
 * Fire-and-forget event tracking. Events are queued (see queue.ts) and flushed
 * in batches. Nothing here ever throws into caller code, and no failure is
 * visible to the user.
 */

const FLUSH_AFTER_EVENTS = 10;
const FLUSH_INTERVAL_MS = 5000;
const MAX_PER_FLUSH = 50;
const BEACON_MAX_PER_FLUSH = 30;

interface TrackerContext {
  clientId: string;
  sessionId: string;
  appName: string | null;
}

let currentContext: TrackerContext = { clientId: '', sessionId: '', appName: null };
let enabled = false;
let dntEnabled = false;
let pendingSinceLastFlush = 0;
let flushTimer: ReturnType<typeof setInterval> | null = null;
let listenersInstalled = false;
let isFlushing = false;
let isFlushingBeacon = false;

export const setAnalyticsContext = (next: Partial<TrackerContext>): void => {
  currentContext = { ...currentContext, ...next };
};

const flushOnce = async (): Promise<void> => {
  if (!enabled || dntEnabled) return;
  if (isFlushing) return;
  isFlushing = true;
  try {
    const events = await drainAndDelete(MAX_PER_FLUSH);
    if (events.length === 0) return;
    const { ok, retryable } = await sendBatchFetch(events);
    // A batch the server rejected outright is dropped rather than re-queued —
    // see isRetryableStatus in transport.ts.
    if (!ok && retryable) {
      await reEnqueueMany(events);
    }
  } catch {
    // swallow
  } finally {
    pendingSinceLastFlush = 0;
    isFlushing = false;
  }
};

const flushOnUnload = (): void => {
  if (!enabled || dntEnabled) return;
  if (isFlushingBeacon) return;
  isFlushingBeacon = true;
  try {
    drainAndDelete(BEACON_MAX_PER_FLUSH)
      .then((events) => {
        if (events.length === 0) return;
        const ok = sendBatchBeacon(events);
        if (!ok) {
          void reEnqueueMany(events);
        }
      })
      .catch(() => undefined)
      .finally(() => {
        isFlushingBeacon = false;
      });
  } catch {
    isFlushingBeacon = false;
  }
};

const installListeners = (): void => {
  if (listenersInstalled) return;
  listenersInstalled = true;
  if (typeof window === 'undefined') return;
  try {
    window.addEventListener('pagehide', flushOnUnload, { capture: true });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flushOnUnload();
    });
  } catch {
    // swallow
  }
};

export const initAnalytics = (params: { clientId: string; sessionId: string }): void => {
  try {
    dntEnabled = isDoNotTrackEnabled();
    if (dntEnabled) {
      enabled = false;
      return;
    }
    currentContext = { ...currentContext, clientId: params.clientId, sessionId: params.sessionId };
    enabled = true;
    installListeners();
    if (flushTimer == null) {
      flushTimer = setInterval(() => {
        void flushOnce();
      }, FLUSH_INTERVAL_MS);
    }
  } catch {
    // swallow
  }
};

export const track = (eventName: EventName): void => {
  try {
    if (dntEnabled) return;

    const payload: AnalyticsEventPayload = {
      eventName,
      clientId: currentContext.clientId,
      sessionId: currentContext.sessionId,
      appName: currentContext.appName,
    };

    // GA4 side-channel; our own store is canonical.
    try {
      safeGtagEvent(eventName, { app: payload.appName ?? undefined });
    } catch {
      // swallow
    }

    if (!enabled || !payload.clientId || !payload.sessionId) return;

    void enqueue(payload);
    pendingSinceLastFlush += 1;
    if (pendingSinceLastFlush >= FLUSH_AFTER_EVENTS) {
      pendingSinceLastFlush = 0;
      void flushOnce();
    }
  } catch {
    // swallow — analytics must never throw
  }
};

